import React, { useState, useEffect, FC } from 'react';
import { css } from 'emotion';
import { weaveRed, weaveGreen } from '@weave/platform-ui-components';
import { useAlert } from '@weave/alert-system';
import { NakedUl, SecondaryButton, Text } from '@weave/design-system';
import { isEmpty, omit } from 'lodash';
import { DeviceSection } from './device-section.component';
import { DeviceRowReadOnly } from './device-row-read-only.component';
import { DeviceRowText } from './device-row-text.component';
import {
  DeviceModelNoId,
  DeviceOptionModel,
  DevicePendingChangesModel,
} from '../../../redux/actions/devices/devices.types';
import { useResources } from '../../../helpers/useResources';
import { DeviceRowSelect } from './device-row-select.component';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { theme } from '@weave/theme-original';
import {
  Provision,
  ProvisionAuthentication_Enum,
  ProvisionHealth_Enum,
} from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { formatDate } from '../../../helpers/date';
import { validateName } from 'helpers/validators';

interface Props {
  isRegistered: boolean;
  locationId: string;
  name: string;
  macAddress: string;
  make: string;
  region: string;
  signalingProtocol: string;
  sipDisabled: boolean;
  updating: boolean;
  type: DeviceModelNoId['type'];
  provision?: Provision;
  updateDevice: (changes: DevicePendingChangesModel) => void;
  unlockDevice: () => void;
  isUnlocking: boolean;
}

interface PendingChanges {
  signalingProtocol?: string;
  sipProfile: {
    name?: string;
  };
}

export const DeviceSectionGeneral: FC<Props> = ({
  isRegistered,
  locationId,
  name,
  macAddress,
  make,
  region,
  signalingProtocol,
  sipDisabled,
  updating,
  type,
  provision,
  updateDevice,
  unlockDevice,
  isUnlocking,
}) => {
  const [nameError, setNameError] = useState<string>('');
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({
    sipProfile: {},
  });
  const [isMacAddressCopied, setIsMacAddressCopied] = useState(false);
  const alerts = useAlert();

  useEffect(() => {
    // When the editable device values update, clear pending changes
    setPendingChanges({ sipProfile: {} });
  }, [name, signalingProtocol]);

  const onSave = async () => {
    if (nameError) {
      alerts.error('Invalid device name');
      return;
    }

    updateDevice(pendingChanges);
  };

  const { data: signalingProtocolOptions } = useResources<DeviceOptionModel[]>(
    'support/v1/phone/devices/options/signaling-protocols',
    {
      deps: [locationId],
      suppressFetch: !locationId,
      resourceLabel: 'signaling protocol options',
    }
  );

  const copyMacAddress = () => {
    setIsMacAddressCopied(true);
    setTimeout(() => {
      setIsMacAddressCopied(false);
    }, 3000);
  };

  const showUnlockButton =
    provision?.authentication === ProvisionAuthentication_Enum.UNLOCKED ||
    provision?.authentication === ProvisionAuthentication_Enum.LOCKED;

  return (
    <DeviceSection
      name="General Settings"
      showSaveButton
      hasRowChanges={
        (!isEmpty(pendingChanges.sipProfile) || !!pendingChanges.signalingProtocol) &&
        !updating
      }
      hasValidationError={!!nameError}
      onSaveClick={onSave}
    >
      <NakedUl
        className={css`
          margin: 16px 0 0;
          max-width: 600px;
        `}
      >
        <DeviceRowText
          name="Display Name"
          error={!!nameError}
          helpText={
            nameError || 'Device names cannot be blank or contain invalid characters'
          }
          value={pendingChanges.sipProfile.name ?? name}
          onChange={(value: string) => {
            if (!value) {
              setNameError('Device names cannot be blank');
            } else {
              const validationError = validateName({ value });
              setNameError(validationError);
            }

            if (value === name) {
              // Clear pending changes
              setPendingChanges({
                ...pendingChanges,
                sipProfile: omit(pendingChanges.sipProfile, 'name'),
              });
            } else {
              setPendingChanges({
                ...pendingChanges,
                sipProfile: { ...pendingChanges.sipProfile, name: value },
              });
            }
          }}
        />
        <DeviceRowReadOnly
          name="Registration Status"
          value={
            <>
              <span
                className={css`
                  display: inline-block;
                  border-radius: 50%;
                  height: 8px;
                  width: 8px;
                  background-color: ${isRegistered ? weaveGreen : weaveRed};
                  margin-right: 8px;
                `}
              />
              {isRegistered ? 'Registered' : 'Not Registered'}
            </>
          }
        />
        {type === 'desk_phone' && (
          <DeviceRowReadOnly
            name="Provisioning Status"
            value={
              <>
                <span
                  className={css`
                    display: inline-block;
                    border-radius: 50%;
                    height: 8px;
                    width: 8px;
                    background-color: ${provision?.health ===
                    ProvisionHealth_Enum.PROVISIONING
                      ? theme.colors.success
                      : theme.colors.error};
                    margin-right: 8px;
                  `}
                />
                {provision?.health === ProvisionHealth_Enum.PROVISIONING
                  ? 'Provisioning'
                  : 'Not Provisioning'}
                {', '}
                {provision?.authentication !== ProvisionAuthentication_Enum.DISABLED && (
                  <>
                    {provision?.authentication === ProvisionAuthentication_Enum.LOCKED
                      ? 'Secured provisioning'
                      : provision?.authentication ===
                        ProvisionAuthentication_Enum.UNLOCKED
                      ? `Unlocked provisioning until ${formatDate(
                          provision?.authenticationEnforcedAt,
                          'MMM D, YYYY; hh:mm A z'
                        )}`
                      : 'Authentication status unknown'}
                    {', '}
                  </>
                )}
                {!!provision?.provisionedAt
                  ? `Last Provisioned on ${formatDate(
                      provision?.provisionedAt,
                      'MMM D, YYYY; hh:mm A z'
                    )}`
                  : 'Unknown provisioned date'}
                <br />

                {showUnlockButton && (
                  <SecondaryButton
                    className={css`
                      width: 190px !important;
                      margin-top: 16px;
                    `}
                    disabled={isUnlocking}
                    onClick={unlockDevice}
                  >
                    Unlock Provisioning
                  </SecondaryButton>
                )}
              </>
            }
          />
        )}
        <DeviceRowReadOnly
          name="SIP Status"
          value={
            <>
              <span
                className={css`
                  display: inline-block;
                  border-radius: 50%;
                  height: 8px;
                  width: 8px;
                  background-color: ${sipDisabled ? weaveRed : weaveGreen};
                  margin-right: 8px;
                `}
              />
              {sipDisabled ? 'Disabled' : 'Enabled'}
            </>
          }
        />
        <DeviceRowReadOnly name="Region" value={region} />
        <DeviceRowReadOnly
          as="div"
          name="MAC Address"
          value={
            <div
              key={macAddress}
              className={css`
                cursor: pointer;
                display: flex;
              `}
            >
              <CopyToClipboard
                onCopy={copyMacAddress}
                text={macAddress.split(':').join('')}
              >
                <span>{macAddress}</span>
              </CopyToClipboard>
              {isMacAddressCopied && (
                <Text
                  className={css`
                    margin-bottom: 0px;
                    margin-left: ${theme.spacing(1)};
                  `}
                  color="success"
                  size="medium"
                >
                  Copied!
                </Text>
              )}
            </div>
          }
        />
        <DeviceRowReadOnly name="Make" value={make} />
        <DeviceRowSelect
          id="signaling-protocol"
          label="Signaling Protocol"
          value={pendingChanges.signalingProtocol ?? signalingProtocol}
          onChange={(newValue: string) => {
            if (newValue === signalingProtocol) {
              // Clear pending changes
              setPendingChanges(omit(pendingChanges, 'signalingProtocol'));
            } else {
              setPendingChanges({ ...pendingChanges, signalingProtocol: newValue });
            }
          }}
          options={signalingProtocolOptions}
        />
      </NakedUl>
    </DeviceSection>
  );
};
